import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, BookOpen, TrendingUp, Plus, Search,
  BarChart3, Calendar, Settings, Bell, UserPlus, Edit, Trash2,
  GraduationCap, Activity, LogOut, Shield, Eye,
  IndianRupee, Megaphone, CheckCircle, AlertCircle, Clock, Filter, Download,
  Video, X, User, Wallet, FileText, MessageCircle, Mail, Phone,
  MoreVertical, RefreshCw, Target, Star, TrendingDown, Youtube, ExternalLink,
  Check, Loader2, Award, Building2, Briefcase, Linkedin, Upload, MapPin,
  Zap, ArrowLeft, FolderOpen, Hash, Gift, Sparkles
} from 'lucide-react';
import FinanceOverview from '../../components/admin/FinanceOverview';
import SiteSettings from '../../components/admin/SiteSettings';
import CallCenter from '../../components/admin/CallCenter';
import LeadsCRM from '../../components/admin/LeadsCRM';
import SiaTraining from '../../components/admin/SiaTraining';
import TeamManagement from '../../components/admin/TeamManagement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Course Modal Component with YouTube/Vimeo Support
const CourseModal = ({ course, courses, mentors, categories = [], onClose, onSave, isNew = false }) => {
  // Convert course data to form format when editing
  const getInitialFormData = () => {
    if (!course) {
      return {
        title: '', description: '', category: 'Foundation', category_id: '', price: 0, duration_months: 6,
        thumbnail: '', skills: [], video_links: [{ title: '', url: '', skill: '', duration_minutes: 10 }], 
        assignments: [], status: 'draft',
        // Default pricing tiers
        pricing_tiers: {
          self_learning: {
            enabled: true,
            name: 'Self-Learning',
            price: 2499,
            original_price: 4999,
            badge: null,
            description: 'Learn at your own pace with full access to course materials',
            cta_text: 'Enroll Now',
            features: [
              { name: 'Self-paced online lessons', included: true, highlight: false },
              { name: 'Structured curriculum', included: true, highlight: false },
              { name: 'Online examinations', included: true, highlight: false },
              { name: 'Full access to learning resources', included: true, highlight: false },
              { name: 'AI learning assistance', included: true, highlight: true },
              { name: 'Monthly group Q&A calls', included: true, highlight: false },
              { name: 'Job assistance resources', included: true, highlight: false },
              { name: 'Personal mentor (1-to-1)', included: false, highlight: false },
              { name: 'Live instructor-led classes', included: false, highlight: false },
              { name: 'Offline classroom access', included: false, highlight: false },
              { name: 'Curated assignment review', included: false, highlight: false },
              { name: 'Portfolio guidance', included: false, highlight: false }
            ]
          },
          instructor_led: {
            enabled: true,
            name: 'Instructor-Led',
            price: 49999,
            original_price: 65000,
            badge: 'Career Track',
            description: 'Complete transformation with personal mentorship and industry connections',
            cta_text: 'Start Your Journey',
            features: [
              { name: 'Self-paced online lessons', included: true, highlight: false },
              { name: 'Structured curriculum', included: true, highlight: false },
              { name: 'Online examinations', included: true, highlight: false },
              { name: 'Full access to learning resources', included: true, highlight: false },
              { name: 'AI learning assistance', included: true, highlight: false },
              { name: 'Monthly group Q&A calls', included: true, highlight: false },
              { name: 'Priority job assistance', included: true, highlight: true },
              { name: 'Personal mentor (1-to-1)', included: true, highlight: true },
              { name: 'Live instructor-led classes', included: true, highlight: true },
              { name: 'Offline classroom access', included: true, highlight: true },
              { name: 'Curated assignment review', included: true, highlight: true },
              { name: 'Portfolio guidance', included: true, highlight: true }
            ]
          }
        }
      };
    }
    
    // Convert existing course to form format
    return {
      title: course.title || '',
      description: course.description || '',
      category: course.category || 'Foundation',
      category_id: course.category_id || '',
      price: course.price || 0,
      // Convert duration_weeks to duration_months or use existing duration_months
      duration_months: course.duration_months || Math.ceil((course.duration_weeks || 12) / 4),
      thumbnail: course.thumbnail || '',
      skills: course.skills || [],
      // Convert modules back to video_links if video_links don't exist
      video_links: course.video_links && course.video_links.length > 0 
        ? course.video_links 
        : (course.modules || []).flatMap(mod => 
            (mod.videos || []).map(v => ({
              title: v.title || '',
              url: v.url || v.youtube_id ? `https://youtube.com/watch?v=${v.youtube_id}` : '',
              skill: mod.title || '',
              duration_minutes: v.duration_minutes || 10
            }))
          ),
      assignments: course.assignments || [],
      status: course.status || (course.is_published ? 'published' : 'draft'),
      pricing_tiers: course.pricing_tiers || null
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);
  const [newSkill, setNewSkill] = useState('');

  const addVideoLink = () => {
    setFormData({...formData, video_links: [...(formData.video_links || []), { title: '', url: '', skill: '', duration_minutes: 10 }]});
  };

  const updateVideoLink = (index, field, value) => {
    const links = [...(formData.video_links || [])];
    links[index] = { ...links[index], [field]: value };
    setFormData({...formData, video_links: links});
  };

  const removeVideoLink = (index) => {
    const links = formData.video_links.filter((_, i) => i !== index);
    setFormData({...formData, video_links: links});
  };

  // Assignment management
  const addAssignment = () => {
    const nextWeek = (formData.assignments?.length || 0) + 1;
    setFormData({
      ...formData, 
      assignments: [...(formData.assignments || []), { 
        title: `Week ${nextWeek} Assignment`, 
        description: '', 
        week_number: nextWeek, 
        due_day: 'Saturday',
        max_score: 100 
      }]
    });
  };

  const updateAssignment = (index, field, value) => {
    const assignments = [...(formData.assignments || [])];
    assignments[index] = { ...assignments[index], [field]: value };
    setFormData({...formData, assignments});
  };

  const removeAssignment = (index) => {
    const assignments = formData.assignments.filter((_, i) => i !== index);
    setFormData({...formData, assignments});
  };

  const addSkill = () => {
    if (newSkill && !formData.skills.includes(newSkill)) {
      setFormData({...formData, skills: [...formData.skills, newSkill]});
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setFormData({...formData, skills: formData.skills.filter(s => s !== skill)});
  };

  const getVideoEmbedUrl = (url) => {
    if (!url) return '';
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            {isNew ? 'Create New Course' : `Edit Course - ${course?.course_id}`}
          </DialogTitle>
          <DialogDescription>
            {isNew ? 'Add a new course to your curriculum' : 'Update course details, pricing, and content'}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[65vh] pr-4">
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Course Title *</Label>
                <Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g., 3D Modeling & Animation" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={3} placeholder="Course description..." />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category_id} onValueChange={v => {
                  const cat = categories.find(c => c.category_id === v);
                  setFormData({...formData, category_id: v, category: cat?.name || formData.category});
                }}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
                <div className="space-y-2">
                  <Label>Duration (months)</Label>
                  <Select value={String(formData.duration_months)} onValueChange={v => setFormData({...formData, duration_months: parseInt(v)})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 months</SelectItem>
                      <SelectItem value="6">6 months</SelectItem>
                      <SelectItem value="9">9 months</SelectItem>
                      <SelectItem value="12">12 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Base Price (₹) - for reference</Label>
                  <Input 
                    type="number" 
                    value={formData.price ?? ''} 
                    onChange={e => setFormData({...formData, price: e.target.value === '' ? '' : parseFloat(e.target.value)})} 
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              <div className="space-y-2 col-span-2">
                <Label>Course Thumbnail</Label>
                <div className="flex gap-3 items-start">
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <Input 
                        value={formData.thumbnail} 
                        onChange={e => setFormData({...formData, thumbnail: e.target.value})} 
                        placeholder="Enter URL or upload an image..." 
                        className="flex-1"
                      />
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) {
                              console.log('No file selected');
                              return;
                            }
                            
                            console.log('File selected:', file.name, file.type, file.size);
                            
                            // Check file size (max 5MB)
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error('Image must be less than 5MB');
                              return;
                            }
                            
                            // Check file type
                            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
                            if (!validTypes.includes(file.type)) {
                              toast.error('Please select a valid image (JPEG, PNG, GIF, or WEBP)');
                              return;
                            }
                            
                            try {
                              toast.loading('Uploading image...', { id: 'upload-toast' });
                              const uploadData = new FormData();
                              uploadData.append('file', file);
                              
                              // Get token directly from localStorage
                              const token = localStorage.getItem('auth_token');
                              console.log('Token exists:', !!token);
                              
                              if (!token) {
                                toast.error('Please login again', { id: 'upload-toast' });
                                return;
                              }
                              
                              console.log('Uploading to:', `${API}/upload/image`);
                              
                              const response = await axios.post(`${API}/upload/image`, uploadData, {
                                headers: { 
                                  'Authorization': `Bearer ${token}`
                                },
                                withCredentials: true
                              });
                              
                              console.log('Upload response:', response.data);
                              
                              // The backend returns /api/uploads/filename, we need to prepend the base URL
                              const baseUrl = API.replace(/\/api\/?$/, '');
                              const imageUrl = `${baseUrl}${response.data.url}`;
                              console.log('Setting thumbnail to:', imageUrl);
                              
                              setFormData(prev => ({...prev, thumbnail: imageUrl}));
                              toast.success('Image uploaded successfully!', { id: 'upload-toast' });
                              
                              // Reset file input
                              e.target.value = '';
                            } catch (err) {
                              console.error('Upload error:', err);
                              console.error('Error response:', err.response?.data);
                              toast.error(err.response?.data?.detail || 'Failed to upload image', { id: 'upload-toast' });
                            }
                          }}
                        />
                        <Button type="button" variant="outline" size="default" className="gap-2 pointer-events-none">
                          <Upload className="w-4 h-4" />
                          Upload
                        </Button>
                      </div>
                      <Button 
                        type="button" 
                        variant="secondary" 
                        size="default" 
                        className="gap-2"
                        onClick={() => {
                          // Random Unsplash images for courses
                          const unsplashImages = [
                            'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800',
                            'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800',
                            'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=800',
                            'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
                            'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800',
                            'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=800',
                            'https://images.unsplash.com/photo-1626785774573-4b799315345d?w=800',
                            'https://images.unsplash.com/photo-1535016120720-40c646be5580?w=800',
                            'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=800',
                            'https://images.unsplash.com/photo-1612178537253-bccd437b730e?w=800',
                            'https://images.unsplash.com/photo-1617791160505-6f00504e3519?w=800',
                            'https://images.unsplash.com/photo-1551269901-5c5e14c25df7?w=800',
                          ];
                          const randomImage = unsplashImages[Math.floor(Math.random() * unsplashImages.length)];
                          setFormData({...formData, thumbnail: randomImage});
                          toast.success('Thumbnail changed!');
                        }}
                      >
                        <RefreshCw className="w-4 h-4" />
                        Change
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Upload from computer, paste a URL, or click Change for random stock image
                    </p>
                  </div>
                  {formData.thumbnail && (
                    <div className="w-28 h-20 rounded-lg overflow-hidden border bg-muted flex-shrink-0">
                      <img 
                        src={formData.thumbnail} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing Tiers Section - Two columns */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold flex items-center gap-2 mb-3">
                <IndianRupee className="w-4 h-4" />
                Pricing Tiers (Self-Learning & Instructor-Led)
              </Label>
              <p className="text-sm text-muted-foreground mb-4">
                Configure pricing and features for both learning paths. Changes save with the course.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                {/* Self-Learning Tier */}
                <Card className="border-blue-500/30">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-blue-500" />
                        Self-Learning
                      </CardTitle>
                      <label className="flex items-center gap-2 text-sm">
                        <input 
                          type="checkbox" 
                          checked={formData.pricing_tiers?.self_learning?.enabled !== false}
                          onChange={e => setFormData({
                            ...formData, 
                            pricing_tiers: {
                              ...formData.pricing_tiers,
                              self_learning: {
                                ...formData.pricing_tiers?.self_learning,
                                enabled: e.target.checked
                              }
                            }
                          })}
                          className="rounded"
                        />
                        Enabled
                      </label>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Price (₹)</Label>
                        <Input 
                          type="number" 
                          value={formData.pricing_tiers?.self_learning?.price ?? ''}
                          onChange={e => setFormData({
                            ...formData,
                            pricing_tiers: {
                              ...formData.pricing_tiers,
                              self_learning: {
                                ...formData.pricing_tiers?.self_learning,
                                price: e.target.value === '' ? '' : parseFloat(e.target.value)
                              }
                            }
                          })}
                          placeholder="2499"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Original (crossed out)</Label>
                        <Input 
                          type="number" 
                          value={formData.pricing_tiers?.self_learning?.original_price ?? ''}
                          onChange={e => setFormData({
                            ...formData,
                            pricing_tiers: {
                              ...formData.pricing_tiers,
                              self_learning: {
                                ...formData.pricing_tiers?.self_learning,
                                original_price: e.target.value === '' ? null : parseFloat(e.target.value)
                              }
                            }
                          })}
                          placeholder="4999"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Textarea 
                        rows={2}
                        value={formData.pricing_tiers?.self_learning?.description || ''}
                        onChange={e => setFormData({
                          ...formData,
                          pricing_tiers: {
                            ...formData.pricing_tiers,
                            self_learning: {
                              ...formData.pricing_tiers?.self_learning,
                              description: e.target.value
                            }
                          }
                        })}
                        placeholder="Learn at your own pace..."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">CTA Button Text</Label>
                      <Input 
                        value={formData.pricing_tiers?.self_learning?.cta_text || 'Enroll Now'}
                        onChange={e => setFormData({
                          ...formData,
                          pricing_tiers: {
                            ...formData.pricing_tiers,
                            self_learning: {
                              ...formData.pricing_tiers?.self_learning,
                              cta_text: e.target.value
                            }
                          }
                        })}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Instructor-Led Tier */}
                <Card className="border-emerald-500/30">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-emerald-500" />
                        Instructor-Led
                      </CardTitle>
                      <label className="flex items-center gap-2 text-sm">
                        <input 
                          type="checkbox" 
                          checked={formData.pricing_tiers?.instructor_led?.enabled !== false}
                          onChange={e => setFormData({
                            ...formData, 
                            pricing_tiers: {
                              ...formData.pricing_tiers,
                              instructor_led: {
                                ...formData.pricing_tiers?.instructor_led,
                                enabled: e.target.checked
                              }
                            }
                          })}
                          className="rounded"
                        />
                        Enabled
                      </label>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Price (₹)</Label>
                        <Input 
                          type="number" 
                          value={formData.pricing_tiers?.instructor_led?.price ?? ''}
                          onChange={e => setFormData({
                            ...formData,
                            pricing_tiers: {
                              ...formData.pricing_tiers,
                              instructor_led: {
                                ...formData.pricing_tiers?.instructor_led,
                                price: e.target.value === '' ? '' : parseFloat(e.target.value)
                              }
                            }
                          })}
                          placeholder="49999"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Original (crossed out)</Label>
                        <Input 
                          type="number" 
                          value={formData.pricing_tiers?.instructor_led?.original_price ?? ''}
                          onChange={e => setFormData({
                            ...formData,
                            pricing_tiers: {
                              ...formData.pricing_tiers,
                              instructor_led: {
                                ...formData.pricing_tiers?.instructor_led,
                                original_price: e.target.value === '' ? null : parseFloat(e.target.value)
                              }
                            }
                          })}
                          placeholder="65000"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Badge Text</Label>
                      <Input 
                        value={formData.pricing_tiers?.instructor_led?.badge || 'Career Track'}
                        onChange={e => setFormData({
                          ...formData,
                          pricing_tiers: {
                            ...formData.pricing_tiers,
                            instructor_led: {
                              ...formData.pricing_tiers?.instructor_led,
                              badge: e.target.value
                            }
                          }
                        })}
                        placeholder="Career Track, Most Popular, etc."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Textarea 
                        rows={2}
                        value={formData.pricing_tiers?.instructor_led?.description || ''}
                        onChange={e => setFormData({
                          ...formData,
                          pricing_tiers: {
                            ...formData.pricing_tiers,
                            instructor_led: {
                              ...formData.pricing_tiers?.instructor_led,
                              description: e.target.value
                            }
                          }
                        })}
                        placeholder="Complete transformation with mentorship..."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">CTA Button Text</Label>
                      <Input 
                        value={formData.pricing_tiers?.instructor_led?.cta_text || 'Start Your Journey'}
                        onChange={e => setFormData({
                          ...formData,
                          pricing_tiers: {
                            ...formData.pricing_tiers,
                            instructor_led: {
                              ...formData.pricing_tiers?.instructor_led,
                              cta_text: e.target.value
                            }
                          }
                        })}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Modules Section */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold flex items-center gap-2 mb-3">
                <Target className="w-4 h-4" />
                Course Modules (Sequential Learning Path)
              </Label>
              <p className="text-sm text-muted-foreground mb-3">Define modules like Modelling, Texturing, Lighting - students complete them in order</p>
              
              <div className="flex flex-wrap gap-2 mb-3">
                {(formData.skills || []).map((skill, idx) => (
                  <Badge key={idx} variant="secondary" className="px-3 py-1">
                    {skill}
                    <X className="w-3 h-3 ml-2 cursor-pointer" onClick={() => removeSkill(skill)} />
                  </Badge>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input value={newSkill} onChange={e => setNewSkill(e.target.value)} placeholder="Add module (e.g., Modelling)" className="w-48" onKeyPress={e => e.key === 'Enter' && addSkill()} />
                <Button type="button" variant="outline" size="sm" onClick={addSkill}>
                  <Plus className="w-4 h-4 mr-1" /> Add Module
                </Button>
              </div>
            </div>

            {/* Module Content Section */}
            <div className="border-t pt-4">
              <div className="mb-4">
                <Label className="text-base font-semibold flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4" />
                  Module Content (Videos & Assignments)
                </Label>
                <p className="text-sm text-muted-foreground">
                  Add videos and assignments to each module. Students complete modules sequentially - 
                  <span className="text-orange-500 font-medium"> Module 2 unlocks after Module 1 is completed.</span>
                </p>
              </div>
              
              {/* Skills/Modules Overview */}
              {/* Display modules with content */}
              {(formData.skills || []).length > 0 ? (
                <div className="space-y-6">
                  {(formData.skills || []).map((skill, skillIdx) => {
                    const skillVideos = (formData.video_links || []).filter(v => v.skill === skill);
                    const skillAssignments = (formData.assignments || []).filter(a => a.skill === skill);
                    
                    return (
                      <Card key={skill} className="overflow-hidden">
                        {/* Module Header */}
                        <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-4 border-b">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center font-bold text-primary">
                                M{skillIdx + 1}
                              </div>
                              <div>
                                <h3 className="font-bold text-lg">{skill}</h3>
                                <p className="text-sm text-muted-foreground">
                                  {skillVideos.length} videos • {skillAssignments.length} assignments
                                </p>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {skillIdx === 0 ? 'Unlocked by default' : `Unlocks after ${formData.skills[skillIdx - 1]}`}
                            </Badge>
                          </div>
                        </div>
                        
                        <CardContent className="p-4 space-y-4">
                          {/* Videos for this skill */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <Label className="text-sm font-medium flex items-center gap-2">
                                <Video className="w-4 h-4 text-red-500" />
                                Videos for {skill}
                              </Label>
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm" 
                                onClick={() => {
                                  setFormData({
                                    ...formData, 
                                    video_links: [...(formData.video_links || []), { 
                                      title: '', 
                                      url: '', 
                                      skill: skill, 
                                      duration_minutes: 10,
                                      order: skillVideos.length + 1
                                    }]
                                  });
                                }}
                              >
                                <Plus className="w-3 h-3 mr-1" /> Add Video to {skill}
                              </Button>
                            </div>
                            
                            {skillVideos.length > 0 ? (
                              <div className="space-y-2">
                                {skillVideos.map((video, videoIdx) => {
                                  const globalIdx = (formData.video_links || []).findIndex(
                                    v => v.skill === skill && v.title === video.title && v.url === video.url
                                  );
                                  return (
                                    <div key={videoIdx} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                                      <div className="w-8 h-8 rounded bg-red-500/10 flex items-center justify-center text-xs font-medium text-red-600">
                                        {videoIdx + 1}
                                      </div>
                                      <Input 
                                        className="flex-1" 
                                        placeholder="Video title" 
                                        value={video.title}
                                        onChange={e => updateVideoLink(globalIdx, 'title', e.target.value)}
                                      />
                                      <Input 
                                        className="flex-1" 
                                        placeholder="YouTube/Vimeo URL" 
                                        value={video.url}
                                        onChange={e => updateVideoLink(globalIdx, 'url', e.target.value)}
                                      />
                                      <Input 
                                        className="w-20" 
                                        type="number" 
                                        placeholder="Min" 
                                        value={video.duration_minutes}
                                        onChange={e => updateVideoLink(globalIdx, 'duration_minutes', parseInt(e.target.value) || 10)}
                                      />
                                      {video.url && (
                                        <a href={video.url} target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-600">
                                          <Youtube className="w-5 h-5" />
                                        </a>
                                      )}
                                      <Button type="button" variant="ghost" size="icon" onClick={() => removeVideoLink(globalIdx)}>
                                        <X className="w-4 h-4 text-destructive" />
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-6 bg-muted/20 rounded-lg border border-dashed">
                                <Video className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground">No videos added for {skill}</p>
                              </div>
                            )}
                          </div>
                          
                          {/* Weekly Assignments Section */}
                          <div className="border-t pt-4">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <Label className="text-sm font-medium flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-blue-500" />
                                  Weekly Assignments for {skill}
                                </Label>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Up to 5 assignments per week • Saturday deadline each week
                                </p>
                              </div>
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm" 
                                onClick={() => {
                                  const existingForSkill = (formData.assignments || []).filter(a => a.skill === skill).length;
                                  const weekNum = Math.floor(existingForSkill / 5) + 1;
                                  const assignmentInWeek = (existingForSkill % 5) + 1;
                                  
                                  if (assignmentInWeek > 5) {
                                    toast.error('Maximum 5 assignments per week. Add a new week.');
                                    return;
                                  }
                                  
                                  setFormData({
                                    ...formData, 
                                    assignments: [...(formData.assignments || []), { 
                                      title: `Week ${weekNum} - Assignment ${assignmentInWeek}`, 
                                      description: '', 
                                      skill: skill,
                                      week_number: weekNum,
                                      assignment_number: assignmentInWeek,
                                      deadline_day: 'Saturday',
                                      max_score: 100 
                                    }]
                                  });
                                }}
                              >
                                <Plus className="w-3 h-3 mr-1" /> Add Assignment
                              </Button>
                            </div>
                            
                            {skillAssignments.length > 0 ? (
                              <div className="space-y-4">
                                {/* Group by week */}
                                {(() => {
                                  const weekGroups = {};
                                  skillAssignments.forEach((a, idx) => {
                                    const week = a.week_number || 1;
                                    if (!weekGroups[week]) weekGroups[week] = [];
                                    weekGroups[week].push({ ...a, localIdx: idx });
                                  });
                                  
                                  return Object.entries(weekGroups).map(([weekNum, weekAssignments]) => (
                                    <div key={weekNum} className="border border-blue-500/20 rounded-lg overflow-hidden">
                                      <div className="bg-blue-500/10 px-3 py-2 flex items-center justify-between">
                                        <span className="text-sm font-semibold text-blue-600">
                                          Week {weekNum}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                          {weekAssignments.length}/5 assignments • Due: Saturday
                                        </span>
                                      </div>
                                      <div className="p-3 space-y-2">
                                        {weekAssignments.map((assignment, assignIdx) => {
                                          const globalIdx = (formData.assignments || []).findIndex(
                                            a => a.skill === skill && a.title === assignment.title && a.week_number === assignment.week_number
                                          );
                                          return (
                                            <div key={assignIdx} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                                              <div className="w-7 h-7 rounded bg-blue-500/10 flex items-center justify-center text-xs font-medium text-blue-600 flex-shrink-0">
                                                {assignment.assignment_number || assignIdx + 1}
                                              </div>
                                              <Input 
                                                className="flex-1 h-8 text-sm" 
                                                placeholder="Assignment title" 
                                                value={assignment.title}
                                                onChange={e => updateAssignment(globalIdx, 'title', e.target.value)}
                                              />
                                              <Input 
                                                className="w-40 h-8 text-sm" 
                                                placeholder="Description" 
                                                value={assignment.description}
                                                onChange={e => updateAssignment(globalIdx, 'description', e.target.value)}
                                              />
                                              <Input 
                                                className="w-16 h-8 text-sm" 
                                                type="number" 
                                                placeholder="Score" 
                                                value={assignment.max_score}
                                                onChange={e => updateAssignment(globalIdx, 'max_score', parseInt(e.target.value) || 100)}
                                              />
                                              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeAssignment(globalIdx)}>
                                                <X className="w-3 h-3 text-destructive" />
                                              </Button>
                                            </div>
                                          );
                                        })}
                                        {weekAssignments.length < 5 && (
                                          <button
                                            type="button"
                                            className="w-full py-2 border border-dashed border-blue-500/30 rounded-lg text-xs text-blue-500 hover:bg-blue-500/5 transition-colors"
                                            onClick={() => {
                                              const assignmentNum = weekAssignments.length + 1;
                                              setFormData({
                                                ...formData, 
                                                assignments: [...(formData.assignments || []), { 
                                                  title: `Week ${weekNum} - Assignment ${assignmentNum}`, 
                                                  description: '', 
                                                  skill: skill,
                                                  week_number: parseInt(weekNum),
                                                  assignment_number: assignmentNum,
                                                  deadline_day: 'Saturday',
                                                  max_score: 100 
                                                }]
                                              });
                                            }}
                                          >
                                            + Add Assignment {weekAssignments.length + 1} of 5
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ));
                                })()}
                                
                                {/* Add New Week Button */}
                                <button
                                  type="button"
                                  className="w-full py-3 border-2 border-dashed border-blue-500/30 rounded-lg text-sm text-blue-500 hover:bg-blue-500/5 transition-colors flex items-center justify-center gap-2"
                                  onClick={() => {
                                    const maxWeek = Math.max(...(skillAssignments.map(a => a.week_number || 1)), 0);
                                    const newWeek = maxWeek + 1;
                                    setFormData({
                                      ...formData, 
                                      assignments: [...(formData.assignments || []), { 
                                        title: `Week ${newWeek} - Assignment 1`, 
                                        description: '', 
                                        skill: skill,
                                        week_number: newWeek,
                                        assignment_number: 1,
                                        deadline_day: 'Saturday',
                                        max_score: 100 
                                      }]
                                    });
                                  }}
                                >
                                  <Plus className="w-4 h-4" />
                                  Add New Week
                                </button>
                              </div>
                            ) : (
                              <div className="text-center py-6 bg-blue-500/5 rounded-lg border border-dashed border-blue-500/20">
                                <FileText className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground mb-3">No assignments for {skill}</p>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    setFormData({
                                      ...formData, 
                                      assignments: [...(formData.assignments || []), { 
                                        title: `Week 1 - Assignment 1`, 
                                        description: '', 
                                        skill: skill,
                                        week_number: 1,
                                        assignment_number: 1,
                                        deadline_day: 'Saturday',
                                        max_score: 100 
                                      }]
                                    });
                                  }}
                                >
                                  <Plus className="w-3 h-3 mr-1" /> Add Week 1 Assignments
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Card className="p-8 text-center border-dashed">
                  <BookOpen className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                  <h3 className="font-medium mb-2">Add Modules First</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Add modules above (e.g., Modelling, Texturing, Lighting) to create the course structure.<br/>
                    Students will complete each module sequentially before unlocking the next.
                  </p>
                </Card>
              )}
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)} disabled={!formData.title}>
            {isNew ? 'Create Course' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Category Create/Edit Modal
const CategoryModal = ({ category, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    course_id_prefix: category?.course_id_prefix || 'VIA_',
    icon: category?.icon || 'BookOpen',
    order: category?.order ?? 0
  });

  const iconOptions = [
    { value: 'Zap', label: 'Zap (Lightning)' },
    { value: 'Briefcase', label: 'Briefcase (Job)' },
    { value: 'GraduationCap', label: 'Graduation Cap' },
    { value: 'BookOpen', label: 'Book' },
    { value: 'Star', label: 'Star' },
    { value: 'Target', label: 'Target' },
    { value: 'Award', label: 'Award' },
    { value: 'Users', label: 'Users' },
  ];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{category ? 'Edit Category' : 'Create New Category'}</DialogTitle>
          <DialogDescription>
            {category ? 'Update category details' : 'Add a new course category'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Category Name *</Label>
            <Input
              data-testid="category-name-input"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Crash Courses"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              data-testid="category-description-input"
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              rows={2}
              placeholder="Brief description of this category..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Course ID Prefix *</Label>
              <Input
                data-testid="category-prefix-input"
                value={formData.course_id_prefix}
                onChange={e => setFormData({...formData, course_id_prefix: e.target.value})}
                placeholder="e.g., VIA_CRSH_"
              />
              <p className="text-xs text-muted-foreground">Courses will be named: {formData.course_id_prefix}001, {formData.course_id_prefix}002...</p>
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <Select value={formData.icon} onValueChange={v => setFormData({...formData, icon: v})}>
                <SelectTrigger data-testid="category-icon-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {iconOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Display Order</Label>
            <Input
              type="number"
              data-testid="category-order-input"
              value={formData.order}
              onChange={e => setFormData({...formData, order: parseInt(e.target.value) || 0})}
              placeholder="0"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="save-category-btn" onClick={() => onSave(formData)} disabled={!formData.name || !formData.course_id_prefix}>
            {category ? 'Save Changes' : 'Create Category'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Offer Create/Edit Modal
const OfferModal = ({ offer, courses, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: offer?.name || '',
    description: offer?.description || '',
    required_courses: offer?.required_courses || [],
    discount_amount: offer?.discount_amount || 0,
    free_course_id: offer?.free_course_id || '',
    active: offer?.active ?? true
  });

  const toggleCourse = (cid) => {
    setFormData(f => ({
      ...f,
      required_courses: f.required_courses.includes(cid)
        ? f.required_courses.filter(id => id !== cid)
        : [...f.required_courses, cid]
    }));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{offer ? 'Edit Offer' : 'Create New Offer'}</DialogTitle>
          <DialogDescription>Configure a combo deal or discount for students</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2"><Label>Offer Name *</Label><Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g., VFX + AI Film Making Combo" data-testid="offer-name-input" /></div>
          <div className="space-y-2"><Label>Description *</Label><Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} placeholder="Describe the offer..." /></div>
          <div className="space-y-2">
            <Label>Required Courses (student must select all to activate offer)</Label>
            <div className="max-h-40 overflow-y-auto space-y-1 border rounded-lg p-2">
              {courses.map(c => (
                <label key={c.course_id} className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-sm ${formData.required_courses.includes(c.course_id) ? 'bg-primary/10' : 'hover:bg-muted'}`}>
                  <Checkbox checked={formData.required_courses.includes(c.course_id)} onCheckedChange={() => toggleCourse(c.course_id)} />
                  <span className="font-mono text-xs">{c.course_id}</span> - {c.title}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Discount Amount (Rs.)</Label><Input type="number" value={formData.discount_amount} onChange={e => setFormData({...formData, discount_amount: parseFloat(e.target.value) || 0})} /></div>
            <div className="space-y-2"><Label>Free Course (optional)</Label>
              <Select value={formData.free_course_id} onValueChange={v => setFormData({...formData, free_course_id: v})}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {courses.map(c => <SelectItem key={c.course_id} value={c.course_id}>{c.course_id} - {c.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-2"><Checkbox checked={formData.active} onCheckedChange={v => setFormData({...formData, active: v})} /><span className="text-sm">Active</span></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave({...formData, free_course_id: formData.free_course_id === 'none' ? null : formData.free_course_id})} disabled={!formData.name || formData.required_courses.length === 0} data-testid="save-offer-btn">{offer ? 'Save' : 'Create Offer'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Mentor Assignment Modal - Enhanced with skill-based matching
const MentorAssignmentModal = ({ course, mentors, existingAssignments, onClose, onAssign }) => {
  const [selectedAssignments, setSelectedAssignments] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get available skills (not yet assigned)
  const availableSkills = (course?.skills || []).filter(skill => 
    !existingAssignments.some(a => a.skill === skill)
  );

  // Filter mentors who have skills matching the course skills
  const getMentorsForSkill = (skill) => {
    return mentors.filter(m => {
      const mentorSkills = (m.skills || m.subjects || []).map(s => s.toLowerCase());
      return mentorSkills.includes(skill.toLowerCase());
    });
  };

  // Toggle mentor selection for a skill
  const toggleMentorSelection = (skill, mentorId, mentorName) => {
    setSelectedAssignments(prev => {
      const current = prev[skill] || [];
      const exists = current.some(m => m.mentorId === mentorId);
      
      if (exists) {
        return { ...prev, [skill]: current.filter(m => m.mentorId !== mentorId) };
      } else {
        return { ...prev, [skill]: [...current, { mentorId, mentorName }] };
      }
    });
  };

  // Check if a mentor is selected for a skill
  const isMentorSelected = (skill, mentorId) => {
    return (selectedAssignments[skill] || []).some(m => m.mentorId === mentorId);
  };

  // Handle batch assignment
  const handleBatchAssign = async () => {
    setIsSubmitting(true);
    try {
      // Assign all selected mentors for each skill
      for (const skill of Object.keys(selectedAssignments)) {
        for (const { mentorId } of selectedAssignments[skill]) {
          await onAssign(mentorId, skill);
        }
      }
      onClose();
    } catch (err) {
      console.error('Assignment error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Count total selections
  const totalSelections = Object.values(selectedAssignments).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Assign Mentors to {course?.title}
          </DialogTitle>
          <DialogDescription>
            Select mentors for each skill based on their expertise. Multiple mentors can teach the same skill.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Current Assignments */}
          {existingAssignments.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Current Assignments
              </Label>
              <div className="grid grid-cols-1 gap-2">
                {existingAssignments.map((a, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="font-medium">{a.mentor_name}</span>
                    </div>
                    <Badge className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30">{a.skill}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available Skills with Mentor Options */}
          {availableSkills.length > 0 ? (
            <div className="space-y-4">
              <Label className="flex items-center gap-2 text-base">
                <BookOpen className="w-4 h-4" />
                Available Skills - Select Mentors
              </Label>
              
              {availableSkills.map(skill => {
                const matchingMentors = getMentorsForSkill(skill);
                const selectedCount = (selectedAssignments[skill] || []).length;
                
                return (
                  <div key={skill} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-sm font-medium bg-primary/5">
                          {skill}
                        </Badge>
                        {selectedCount > 0 && (
                          <Badge className="bg-emerald-500/10 text-emerald-600 text-xs">
                            {selectedCount} selected
                          </Badge>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {matchingMentors.length} mentor{matchingMentors.length !== 1 ? 's' : ''} available
                      </span>
                    </div>
                    
                    {matchingMentors.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {matchingMentors.map(mentor => {
                          const isSelected = isMentorSelected(skill, mentor.user_id);
                          return (
                            <div
                              key={mentor.user_id}
                              onClick={() => toggleMentorSelection(skill, mentor.user_id, mentor.name)}
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border ${
                                isSelected 
                                  ? 'bg-primary/10 border-primary/30 ring-1 ring-primary/20' 
                                  : 'bg-muted/30 border-muted hover:bg-muted/50'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'
                              }`}>
                                {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                              </div>
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                                <span className="text-sm font-medium">{mentor.name?.charAt(0) || 'M'}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{mentor.name}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {(mentor.skills || mentor.subjects || []).slice(0, 3).join(', ')}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground bg-muted/30 rounded-lg">
                        <AlertCircle className="w-5 h-5 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No mentors found with &quot;{skill}&quot; expertise</p>
                        <p className="text-xs mt-1">Add mentors with this skill in the Mentors tab</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground bg-muted/30 rounded-lg">
              <CheckCircle className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
              <p className="font-medium">All skills have been assigned mentors</p>
              <p className="text-sm mt-1">You can view current assignments above</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {totalSelections > 0 && `${totalSelections} mentor${totalSelections !== 1 ? 's' : ''} selected`}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button 
              onClick={handleBatchAssign} 
              disabled={totalSelections === 0 || isSubmitting}
              className="bg-gradient-to-r from-emerald-500 to-cyan-500"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign {totalSelections > 0 ? `(${totalSelections})` : ''} Mentor{totalSelections !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Student Modal
const StudentModal = ({ student, courses, mentors, onClose, onSave, isNew = false }) => {
  // Get course_id from existing enrollments
  const existingCourseId = student?.enrollments?.[0]?.course_id || '';
  
  const [formData, setFormData] = useState({
    name: student?.name || '',
    email: student?.email || '',
    phone: student?.phone || '',
    course_id: existingCourseId,
    batch: student?.batch || 'Morning A',
    total_fees: student?.total_fees || 0,
    status: student?.status || 'active',
    guardian_name: student?.guardian_name || '',
    guardian_phone: student?.guardian_phone || '',
    address: student?.address || '',
    city: student?.city || '',
    pincode: student?.pincode || '',
    date_of_birth: student?.date_of_birth || '',
    mentor_id: student?.assigned_mentor_id || ''
  });

  // Get active mentors
  const activeMentors = (mentors || []).filter(m => m.status === 'active' || !m.status);
  
  // Check if this is a test account
  const isTestAccount = student?.is_test_account || 
    (student?.email && (
      student.email.includes('@example.com') ||
      student.email.includes('@test.com') ||
      student.email.toLowerCase().startsWith('test')
    ));

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5" />
            {isNew ? 'Add New Student' : 'Edit Student Profile'}
            {!isNew && isTestAccount && (
              <Badge variant="outline" className="ml-2 bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
                TEST ACCOUNT
              </Badge>
            )}
            {!isNew && !isTestAccount && student?.registration_source === 'organic' && (
              <Badge variant="outline" className="ml-2 bg-green-500/10 text-green-600 border-green-500/30">
                VERIFIED STUDENT
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {isNew ? 'Create a new student account' : 'Update student information and assign mentor'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Personal Information */}
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Personal Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Student's full name" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="email@example.com" />
              </div>
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="+91 9999999999" />
              </div>
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input type="date" value={formData.date_of_birth || ''} onChange={e => setFormData({...formData, date_of_birth: e.target.value})} />
              </div>
            </div>
          </div>

          {/* Guardian Information */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Guardian / Parent Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Guardian Name</Label>
                <Input value={formData.guardian_name || ''} onChange={e => setFormData({...formData, guardian_name: e.target.value})} placeholder="Parent/Guardian name" />
              </div>
              <div className="space-y-2">
                <Label>Guardian Phone</Label>
                <Input value={formData.guardian_phone || ''} onChange={e => setFormData({...formData, guardian_phone: e.target.value})} placeholder="+91 9999999999" />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Address
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Full Address</Label>
                <Input value={formData.address || ''} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="House/Flat No., Street, Area" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input value={formData.city || ''} onChange={e => setFormData({...formData, city: e.target.value})} placeholder="City" />
                </div>
                <div className="space-y-2">
                  <Label>Pincode</Label>
                  <Input value={formData.pincode || ''} onChange={e => setFormData({...formData, pincode: e.target.value})} placeholder="000000" />
                </div>
              </div>
            </div>
          </div>

          {/* Course & Academic Information */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Academic Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Course *</Label>
                <Select value={formData.course_id} onValueChange={v => setFormData({...formData, course_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>
                    {courses.filter(c => c.status !== 'archived').map(c => (
                      <SelectItem key={c.course_id} value={c.course_id}>{c.title} ({c.course_id})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Batch</Label>
                <Select value={formData.batch} onValueChange={v => setFormData({...formData, batch: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Morning A">Morning A</SelectItem>
                    <SelectItem value="Morning B">Morning B</SelectItem>
                    <SelectItem value="Evening A">Evening A</SelectItem>
                    <SelectItem value="Evening B">Evening B</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Total Fees (₹)</Label>
                <Input type="number" value={formData.total_fees} onChange={e => setFormData({...formData, total_fees: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="graduated">Graduated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Mentor Assignment */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" />
              Assign Mentor
            </h3>
            <div className="space-y-2">
              <Label>Select Mentor</Label>
              <Select 
                value={formData.mentor_id || formData.assigned_mentor_id || 'none'} 
                onValueChange={v => setFormData({...formData, mentor_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger className={formData.mentor_id || formData.assigned_mentor_id ? '' : 'border-amber-500'}>
                  <SelectValue placeholder="⚠️ No mentor assigned - Select a mentor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Mentor</SelectItem>
                  {activeMentors.filter(m => m.user_id).map(m => (
                    <SelectItem key={m.user_id} value={m.user_id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{m.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({m.skills?.join(', ') || m.subjects?.join(', ') || 'General'})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!(formData.mentor_id || formData.assigned_mentor_id) && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Assigning a mentor helps students get personalized guidance
                </p>
              )}
            </div>
          </div>
        </div>

        {isNew && (
          <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
            Default password will be: <code className="bg-background px-2 py-1 rounded font-mono">Welcome123!</code>
          </p>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)} disabled={!formData.name || !formData.email || !formData.course_id}>
            {isNew ? 'Create Student' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Student Detail Modal
const StudentDetailModal = ({ student, onClose }) => {
  if (!student) return null;
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarFallback className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                {student.name?.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-xl font-bold">{student.name}</p>
              <p className="text-sm text-muted-foreground font-normal">{student.user_id}</p>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center p-3 bg-blue-500/10 rounded-lg">
              <p className="text-2xl font-bold text-blue-500">{student.attendance || 0}%</p>
              <p className="text-xs text-muted-foreground">Attendance</p>
            </div>
            <div className="text-center p-3 bg-emerald-500/10 rounded-lg">
              <p className="text-2xl font-bold text-emerald-500">{student.gpa || 0}</p>
              <p className="text-xs text-muted-foreground">GPA</p>
            </div>
            <div className="text-center p-3 bg-purple-500/10 rounded-lg">
              <p className="text-2xl font-bold text-purple-500">₹{((student.fees_paid || 0)/1000).toFixed(0)}K</p>
              <p className="text-xs text-muted-foreground">Fees Paid</p>
            </div>
            <div className="text-center p-3 bg-orange-500/10 rounded-lg">
              <p className="text-2xl font-bold text-orange-500">₹{((student.pending_fees || 0)/1000).toFixed(0)}K</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
          </div>

          {/* Contact Information */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-muted-foreground" /> {student.email}</p>
              <p className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-muted-foreground" /> {student.phone || 'Not provided'}</p>
              <p className="flex items-center gap-2 text-sm"><Calendar className="w-4 h-4 text-muted-foreground" /> Enrolled: {student.created_at ? new Date(student.created_at).toLocaleDateString('en-IN') : 'N/A'}</p>
              {student.date_of_birth && (
                <p className="flex items-center gap-2 text-sm"><Calendar className="w-4 h-4 text-muted-foreground" /> DOB: {new Date(student.date_of_birth).toLocaleDateString('en-IN')}</p>
              )}
            </CardContent>
          </Card>

          {/* Guardian Information */}
          {(student.guardian_name || student.guardian_phone) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Guardian / Parent Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {student.guardian_name && (
                  <p className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-muted-foreground" /> {student.guardian_name}</p>
                )}
                {student.guardian_phone && (
                  <p className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-muted-foreground" /> {student.guardian_phone}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Address */}
          {(student.address || student.city) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">
                  {student.address && <span>{student.address}, </span>}
                  {student.city && <span>{student.city} </span>}
                  {student.pincode && <span>- {student.pincode}</span>}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Assigned Mentor */}
          <Card className={student.assigned_mentor_id ? '' : 'border-amber-500'}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="w-4 h-4 text-amber-500" />
                Assigned Mentor
              </CardTitle>
            </CardHeader>
            <CardContent>
              {student.assigned_mentor_id || student.mentor_name ? (
                <div className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-lg">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                      {(student.mentor_name || 'M')?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{student.mentor_name || student.assigned_mentor_id}</p>
                    <p className="text-xs text-muted-foreground">Active Mentor</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-amber-600 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  No mentor assigned yet. Edit student to assign a mentor.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Enrolled Courses */}
          {student.enrollments?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Enrolled Courses</CardTitle>
              </CardHeader>
              <CardContent>
                {student.enrollments.map((e, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 border rounded-lg mb-2">
                    <span>{e.course_title || e.course_id}</span>
                    <Badge>{Math.round(e.progress || 0)}% complete</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button><MessageCircle className="w-4 h-4 mr-2" /> Send Message</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [stats, setStats] = useState({ total_students: 0, total_teachers: 0, total_courses: 0 });
  const [students, setStudents] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [courses, setCourses] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [alumni, setAlumni] = useState([]);
  const [finances, setFinances] = useState({});
  const [pendingTasks, setPendingTasks] = useState({ pending_mentor_assignments: [], pending_notifications: [], total_pending: 0 });
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [coupons, setCoupons] = useState([]);
  
  // Category states
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Admissions states
  const [admissions, setAdmissions] = useState([]);
  const [adminOffers, setAdminOffers] = useState([]);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  
  // Modal states
  const [editingStudent, setEditingStudent] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [assigningMentor, setAssigningMentor] = useState(null);
  const [assigningMentorToEnrollment, setAssigningMentorToEnrollment] = useState(null);
  const [isNewStudent, setIsNewStudent] = useState(false);
  const [isNewCourse, setIsNewCourse] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '', target: 'everyone', status: 'published' });
  const [editingAlumni, setEditingAlumni] = useState(null);
  const [isNewAlumni, setIsNewAlumni] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [isNewCoupon, setIsNewCoupon] = useState(false);
  
  // Delete confirmation states
  const [deletingMentor, setDeletingMentor] = useState(null);
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const cardClass = theme === 'dark' ? 'card-shadow-dark' : 'card-shadow-light';

  // API calls
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadDashboard = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [dashRes, studentsRes, mentorsRes, coursesRes, announcementsRes, alumniRes, financesRes, pendingRes, notifRes, couponsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/students`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/mentors`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/courses`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/announcements`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/alumni`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/finances`, { headers, withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/pending-tasks`, { headers, withCredentials: true }).catch(() => ({ data: { pending_mentor_assignments: [], pending_notifications: [], total_pending: 0 } })),
        axios.get(`${API}/admin/notifications`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/coupons`, { headers, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/categories`, { headers, withCredentials: true }).catch(() => ({ data: [] }))
      ]);

      setStats(dashRes.data.stats);
      setStudents(studentsRes.data);
      setMentors(mentorsRes.data);
      setCourses(coursesRes.data);
      setAnnouncements(announcementsRes.data);
      setAlumni(alumniRes.data);
      setFinances(financesRes.data);
      setPendingTasks(pendingRes.data);
      setAdminNotifications(notifRes.data);
      setCoupons(couponsRes.data);
      setCategories(categoriesRes.data);

      // Fetch admissions and offers separately (non-blocking)
      axios.get(`${API}/admin/admissions`, { headers, withCredentials: true })
        .then(r => setAdmissions(r.data)).catch(() => {});
      axios.get(`${API}/admin/offers`, { headers, withCredentials: true })
        .then(r => setAdminOffers(r.data)).catch(() => {});
      setPendingTasks(pendingRes.data);
      setAdminNotifications(notifRes.data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Create announcement
  const handleCreateAnnouncement = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/announcements`, newAnnouncement, { headers, withCredentials: true });
      toast.success('Announcement published!');
      setShowAnnouncementModal(false);
      setNewAnnouncement({ title: '', content: '', target: 'everyone', status: 'published' });
      loadDashboard();
    } catch (err) {
      toast.error('Failed to create announcement');
    }
  };

  // Delete announcement
  const handleDeleteAnnouncement = async (announcementId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/announcements/${announcementId}`, { headers, withCredentials: true });
      toast.success('Announcement deleted');
      loadDashboard();
    } catch (err) {
      toast.error('Failed to delete announcement');
    }
  };

  // Delete mentor handler
  const handleDeleteMentor = async () => {
    if (!deletingMentor) return;
    setIsDeleting(true);
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/mentors/${deletingMentor.user_id}`, { headers, withCredentials: true });
      toast.success(`Mentor "${deletingMentor.name}" has been deactivated`);
      setDeletingMentor(null);
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete mentor');
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete student handler
  const handleDeleteStudent = async () => {
    if (!deletingStudent) return;
    setIsDeleting(true);
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/students/${deletingStudent.user_id}`, { headers, withCredentials: true });
      toast.success(`Student "${deletingStudent.name}" has been deactivated`);
      setDeletingStudent(null);
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete student');
    } finally {
      setIsDeleting(false);
    }
  };

  // Alumni CRUD
  const handleSaveAlumni = async () => {
    try {
      const headers = getAuthHeaders();
      if (!editingAlumni.name || !editingAlumni.current_company || !editingAlumni.current_role || !editingAlumni.batch_year) {
        toast.error('Please fill all required fields');
        return;
      }
      
      if (isNewAlumni) {
        await axios.post(`${API}/admin/alumni`, editingAlumni, { headers, withCredentials: true });
        toast.success('Alumni added successfully!');
      } else {
        await axios.put(`${API}/admin/alumni/${editingAlumni.alumni_id}`, editingAlumni, { headers, withCredentials: true });
        toast.success('Alumni updated successfully!');
      }
      setEditingAlumni(null);
      setIsNewAlumni(false);
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save alumni');
    }
  };

  const handleDeleteAlumni = async (alumniId) => {
    if (!confirm('Are you sure you want to delete this alumni record?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/alumni/${alumniId}`, { headers, withCredentials: true });
      toast.success('Alumni deleted');
      loadDashboard();
    } catch (err) {
      toast.error('Failed to delete alumni');
    }
  };

  const handleSaveCoupon = async () => {
    if (!editingCoupon.code || !editingCoupon.discount_value) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      const headers = getAuthHeaders();
      if (isNewCoupon) {
        await axios.post(`${API}/admin/coupons`, editingCoupon, { headers, withCredentials: true });
        toast.success('Coupon created successfully');
      } else {
        await axios.put(`${API}/admin/coupons/${editingCoupon.coupon_id}`, editingCoupon, { headers, withCredentials: true });
        toast.success('Coupon updated successfully');
      }
      setEditingCoupon(null);
      setIsNewCoupon(false);
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!confirm('Are you sure you want to delete this coupon?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/coupons/${couponId}`, { headers, withCredentials: true });
      toast.success('Coupon deleted');
      loadDashboard();
    } catch (err) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboard();
  };

  const handleLogout = async () => {
    try { await logout(); navigate('/'); } catch (e) { toast.error('Logout failed'); }
  };

  // Assign mentor to student enrollment
  const handleAssignMentorToEnrollment = async (enrollment, mentorId) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/admin/enrollments/assign-mentor`, {
        enrollment_id: enrollment.enrollment_id,
        mentor_id: mentorId
      }, { headers, withCredentials: true });
      
      toast.success(`${response.data.mentor_name} assigned to ${response.data.student_name}!`);
      setAssigningMentorToEnrollment(null);
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign mentor');
    }
  };

  // Course CRUD
  const handleSaveCourse = async (courseData) => {
    try {
      const headers = getAuthHeaders();
      if (isNewCourse) {
        await axios.post(`${API}/admin/courses`, courseData, { headers, withCredentials: true });
        toast.success('Course created successfully!');
      } else {
        await axios.put(`${API}/admin/courses/${editingCourse.course_id}`, courseData, { headers, withCredentials: true });
        toast.success('Course updated successfully!');
      }
      setEditingCourse(null);
      setIsNewCourse(false);
      handleRefresh();
    } catch (err) {
      // Handle Pydantic validation errors which come as an array
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Failed to save course';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic returns an array of validation errors
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to archive this course?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/courses/${courseId}`, { headers, withCredentials: true });
      toast.success('Course archived');
      handleRefresh();
    } catch (err) {
      toast.error('Failed to archive course');
    }
  };

  const handleMoveCourse = async (courseId, targetCategoryId) => {
    try {
      const headers = getAuthHeaders();
      await axios.patch(`${API}/admin/courses/${courseId}/category`, { category_id: targetCategoryId }, { headers, withCredentials: true });
      const targetCat = categories.find(c => c.category_id === targetCategoryId);
      toast.success(`Course moved to ${targetCat?.name || 'category'}`);
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move course');
    }
  };

  const handleRenameCourse = async (courseId) => {
    const newId = window.prompt(`Enter new course code for ${courseId}:`, courseId);
    if (!newId || newId === courseId) return;
    try {
      const headers = getAuthHeaders();
      const res = await axios.patch(`${API}/admin/courses/${courseId}/rename`, { new_course_id: newId }, { headers, withCredentials: true });
      toast.success(`Course code changed: ${courseId} → ${newId}`);
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename course');
    }
  };

  // Student CRUD
  const handleSaveStudent = async (studentData) => {
    try {
      const headers = getAuthHeaders();
      if (isNewStudent) {
        const res = await axios.post(`${API}/admin/students`, studentData, { headers, withCredentials: true });
        toast.success(`Student created! Default password: ${res.data.default_password}`);
      } else {
        await axios.put(`${API}/admin/students/${editingStudent.user_id}`, studentData, { headers, withCredentials: true });
        toast.success('Student updated!');
      }
      setEditingStudent(null);
      setIsNewStudent(false);
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save student');
    }
  };

  // Mentor Assignment
  const handleAssignMentor = async (mentorId, skill) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/mentors/assign`, {
        mentor_id: mentorId,
        course_id: assigningMentor.course_id,
        skill: skill
      }, { headers, withCredentials: true });
      toast.success('Mentor assigned successfully!');
      setAssigningMentor(null);
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign mentor');
    }
  };

  // Filters
  const filteredStudents = students.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter courses - if a category is selected, show only courses from that category
  const filteredCourses = courses.filter(c => {
    const matchesSearch = c.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.course_id?.toLowerCase().includes(searchQuery.toLowerCase());
    if (selectedCategory) {
      return matchesSearch && c.category_id === selectedCategory.category_id;
    }
    return matchesSearch;
  });

  // Category CRUD
  const handleSaveCategory = async (categoryData) => {
    try {
      const headers = getAuthHeaders();
      if (editingCategory?.category_id) {
        await axios.put(`${API}/admin/categories/${editingCategory.category_id}`, categoryData, { headers, withCredentials: true });
        toast.success('Category updated!');
      } else {
        await axios.post(`${API}/admin/categories`, categoryData, { headers, withCredentials: true });
        toast.success('Category created!');
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure? This only works if no courses are in this category.')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/categories/${categoryId}`, { headers, withCredentials: true });
      toast.success('Category deleted');
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  // Icon mapper for categories
  const getCategoryIcon = (iconName) => {
    const iconMap = { Zap, Briefcase, GraduationCap, BookOpen, Star, Target, Award, Users };
    return iconMap[iconName] || BookOpen;
  };

  // Offer CRUD
  const handleSaveOffer = async (offerData) => {
    try {
      const headers = getAuthHeaders();
      if (editingOffer?.offer_id) {
        await axios.put(`${API}/admin/offers/${editingOffer.offer_id}`, offerData, { headers, withCredentials: true });
        toast.success('Offer updated!');
      } else {
        await axios.post(`${API}/admin/offers`, offerData, { headers, withCredentials: true });
        toast.success('Offer created!');
      }
      setShowOfferModal(false);
      setEditingOffer(null);
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save offer');
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Delete this offer?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/offers/${offerId}`, { headers, withCredentials: true });
      toast.success('Offer deleted');
      handleRefresh();
    } catch (err) {
      toast.error('Failed to delete offer');
    }
  };

  // Sidebar items
  const sidebarItems = [
    { id: 'overview', label: 'Dashboard', icon: BarChart3 },
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'admissions', label: 'Admissions', icon: FileText },
    { id: 'leads', label: 'Leads CRM', icon: Target },
    { id: 'sia-training', label: 'Parul Training', icon: Sparkles },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'students', label: 'Students', icon: GraduationCap },
    { id: 'mentors', label: 'Mentors', icon: Users },
    { id: 'call-center', label: 'Call Center', icon: Phone },
    { id: 'alumni', label: 'Alumni', icon: Award },
    { id: 'finances', label: 'Finances', icon: IndianRupee },
    { id: 'coupons', label: 'Coupons', icon: Wallet },
    { id: 'announcements', label: 'Announcements', icon: Megaphone },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Admin Panel</h1>
              <p className="text-xs text-muted-foreground">Visual Institute of Arts</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 w-64" />
            </div>
            
            <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10 ring-2 ring-red-500/30">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-gradient-to-r from-red-500 to-orange-500 text-white">
                      {user?.name?.split(' ').map(n => n[0]).join('') || 'A'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <DropdownMenuLabel>{user?.name || 'Admin'}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-73px)] bg-card border-r p-4 hidden lg:block">
          <nav className="space-y-1">
            {sidebarItems.map(item => (
              <Button key={item.id} variant={activeTab === item.id ? 'secondary' : 'ghost'} className="w-full justify-start" onClick={() => { setActiveTab(item.id); setSelectedCategory(null); }}>
                <item.icon className="w-4 h-4 mr-3" /> {item.label}
              </Button>
            ))}
          </nav>

          <div className="mt-8 p-4 bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-xl border border-red-500/20">
            <h4 className="font-semibold mb-3 text-sm">Quick Stats</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Students</span><span className="font-bold">{stats.total_students}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Mentors</span><span className="font-bold">{stats.total_teachers}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Courses</span><span className="font-bold">{stats.total_courses}</span></div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <AnimatePresence mode="wait">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Total Students', value: stats.total_students, icon: GraduationCap, color: 'blue' },
                    { label: 'Total Mentors', value: stats.total_teachers, icon: Users, color: 'emerald' },
                    { label: 'Active Courses', value: stats.total_courses, icon: BookOpen, color: 'purple' },
                    { label: 'Revenue', value: `₹${((finances.total_revenue || 0)/100000).toFixed(1)}L`, icon: IndianRupee, color: 'orange' }
                  ].map((stat, i) => (
                    <Card key={i} className={cardClass}>
                      <CardContent className="p-4">
                        <stat.icon className={`w-8 h-8 mb-2 text-${stat.color}-500`} />
                        <p className="text-2xl font-bold">{stat.value}</p>
                        <p className="text-sm text-muted-foreground">{stat.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <Card className={cardClass}>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="flex gap-3 flex-wrap">
                    <Button onClick={() => { setIsNewCourse(true); setEditingCourse({}); }}><Plus className="w-4 h-4 mr-2" /> New Course</Button>
                    <Button variant="outline" onClick={() => setActiveTab('finances')}><IndianRupee className="w-4 h-4 mr-2" /> View Finances</Button>
                    <Button variant="outline" onClick={() => setActiveTab('announcements')}><Megaphone className="w-4 h-4 mr-2" /> Announcements</Button>
                  </CardContent>
                </Card>

                {/* Pending Tasks Alert */}
                {pendingTasks.total_pending > 0 && (
                  <Card className={`${cardClass} border-orange-500/30 bg-orange-500/5`}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-orange-600">
                        <AlertCircle className="w-5 h-5" />
                        Pending Tasks ({pendingTasks.total_pending})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Pending Mentor Assignments */}
                      {pendingTasks.pending_mentor_assignments?.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2 flex items-center gap-2">
                            <UserPlus className="w-4 h-4" />
                            Mentor Assignment Required ({pendingTasks.pending_mentor_assignments.length})
                          </h4>
                          <div className="space-y-2">
                            {pendingTasks.pending_mentor_assignments.slice(0, 5).map(enrollment => (
                              <div key={enrollment.enrollment_id} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                                <div>
                                  <p className="font-medium">{enrollment.student_name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    Enrolled in: {enrollment.course_title || enrollment.course_id}
                                  </p>
                                </div>
                                <Button 
                                  size="sm" 
                                  onClick={() => setAssigningMentorToEnrollment(enrollment)}
                                >
                                  Assign Mentor
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Notifications */}
                      {adminNotifications.filter(n => !n.read).length > 0 && (
                        <div>
                          <h4 className="font-medium mb-2 flex items-center gap-2">
                            <Bell className="w-4 h-4" />
                            Unread Notifications ({adminNotifications.filter(n => !n.read).length})
                          </h4>
                          <div className="space-y-2">
                            {adminNotifications.filter(n => !n.read).slice(0, 3).map(notif => (
                              <div key={notif.notification_id} className="p-3 bg-background rounded-lg border">
                                <p className="font-medium text-sm">{notif.title}</p>
                                <p className="text-xs text-muted-foreground">{notif.message}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <motion.div key="courses" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                {!selectedCategory ? (
                  <>
                    {/* Categories Overview */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-2xl font-bold" data-testid="course-management-title">Course Categories</h2>
                        <p className="text-sm text-muted-foreground mt-1">Select a category to manage its courses</p>
                      </div>
                      <Button data-testid="create-category-btn" onClick={() => { setEditingCategory(null); setShowCategoryModal(true); }}>
                        <Plus className="w-4 h-4 mr-2" /> New Category
                      </Button>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {categories.map(cat => {
                        const IconComp = getCategoryIcon(cat.icon);
                        const catCourses = courses.filter(c => c.category_id === cat.category_id);
                        return (
                          <Card
                            key={cat.category_id}
                            data-testid={`category-card-${cat.category_id}`}
                            className={`${cardClass} cursor-pointer group hover:ring-2 hover:ring-primary/50 transition-all`}
                            onClick={() => setSelectedCategory(cat)}
                          >
                            <CardContent className="p-6">
                              <div className="flex items-start justify-between mb-4">
                                <div className="p-3 rounded-xl bg-primary/10 text-primary">
                                  <IconComp className="w-6 h-6" />
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setEditingCategory(cat); setShowCategoryModal(true); }}>
                                    <Edit className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.category_id); }}>
                                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                  </Button>
                                </div>
                              </div>
                              <h3 className="text-lg font-bold mb-1">{cat.name}</h3>
                              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{cat.description}</p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <BookOpen className="w-4 h-4" />
                                  <span>{catCourses.length} course{catCourses.length !== 1 ? 's' : ''}</span>
                                </div>
                                <Badge variant="outline" className="text-xs font-mono">{cat.course_id_prefix}XXX</Badge>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}

                      {/* Add category placeholder card */}
                      <Card
                        className={`${cardClass} cursor-pointer border-dashed hover:border-primary/50 transition-all`}
                        onClick={() => { setEditingCategory(null); setShowCategoryModal(true); }}
                        data-testid="add-category-card"
                      >
                        <CardContent className="p-6 flex flex-col items-center justify-center h-full min-h-[200px] text-muted-foreground">
                          <Plus className="w-10 h-10 mb-2" />
                          <span className="font-medium">Add Category</span>
                        </CardContent>
                      </Card>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Category Detail - Courses List */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Button variant="ghost" size="icon" data-testid="back-to-categories-btn" onClick={() => setSelectedCategory(null)}>
                          <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                          <h2 className="text-2xl font-bold" data-testid="category-detail-title">{selectedCategory.name}</h2>
                          <p className="text-sm text-muted-foreground">{selectedCategory.description}</p>
                        </div>
                      </div>
                      <Button data-testid="create-course-in-category-btn" onClick={() => { setIsNewCourse(true); setEditingCourse({ category_id: selectedCategory.category_id, category: selectedCategory.name }); }}>
                        <Plus className="w-4 h-4 mr-2" /> Add Course
                      </Button>
                    </div>

                    {filteredCourses.length === 0 ? (
                      <Card className={cardClass}>
                        <CardContent className="p-12 text-center">
                          <FolderOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                          <h3 className="text-lg font-semibold mb-1">No courses yet</h3>
                          <p className="text-sm text-muted-foreground mb-4">Add your first course to this category</p>
                          <Button onClick={() => { setIsNewCourse(true); setEditingCourse({ category_id: selectedCategory.category_id, category: selectedCategory.name }); }}>
                            <Plus className="w-4 h-4 mr-2" /> Create Course
                          </Button>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCourses.map(course => (
                          <Card key={course.course_id} className={cardClass} data-testid={`course-card-${course.course_id}`}>
                            <CardContent className="p-0">
                              {course.thumbnail && (
                                <img src={course.thumbnail} alt={course.title} className="w-full h-32 object-cover rounded-t-lg" />
                              )}
                              <div className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <Badge variant="outline" className="mb-1 font-mono">{course.course_id}</Badge>
                                    <h3 className="font-bold">{course.title}</h3>
                                  </div>
                                  <Badge variant={course.status === 'published' || course.is_published ? 'default' : 'secondary'}>
                                    {course.status === 'published' || course.is_published ? 'Published' : course.status || 'Draft'}
                                  </Badge>
                                </div>

                                {/* Skills */}
                                <div className="flex flex-wrap gap-1 mb-3">
                                  {(course.skills || []).slice(0, 3).map((s, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                                  ))}
                                  {(course.skills || []).length > 3 && <Badge variant="outline" className="text-xs">+{course.skills.length - 3}</Badge>}
                                </div>

                                {/* Mentors */}
                                {course.assigned_mentors?.length > 0 && (
                                  <div className="mb-3 text-sm">
                                    <span className="text-muted-foreground">Mentors: </span>
                                    {course.assigned_mentors.map(a => a.mentor_name).join(', ')}
                                  </div>
                                )}

                                <div className="flex justify-between text-sm text-muted-foreground mb-3">
                                  <span>{course.enrolled_count || 0} students</span>
                                  <span>{'\u20B9'}{((course.price || 0)/1000).toFixed(0)}K</span>
                                </div>

                                <div className="flex gap-2">
                                  <Button variant="outline" size="sm" className="flex-1" onClick={() => { setEditingCourse(course); setIsNewCourse(false); }}>
                                    <Edit className="w-4 h-4 mr-1" /> Edit
                                  </Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="outline" size="sm">
                                        <MoreVertical className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => setAssigningMentor(course)}>
                                        <Users className="w-4 h-4 mr-2" /> Assign Mentor
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleRenameCourse(course.course_id)}>
                                        <Hash className="w-4 h-4 mr-2" /> Change Code
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuLabel className="text-xs text-muted-foreground">Move to Category</DropdownMenuLabel>
                                      {categories.filter(c => c.category_id !== course.category_id).map(cat => (
                                        <DropdownMenuItem key={cat.category_id} onClick={() => handleMoveCourse(course.course_id, cat.category_id)}>
                                          <ArrowLeft className="w-4 h-4 mr-2 rotate-180" /> {cat.name}
                                        </DropdownMenuItem>
                                      ))}
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => handleDeleteCourse(course.course_id)} className="text-destructive focus:text-destructive">
                                        <Trash2 className="w-4 h-4 mr-2" /> Archive
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}


            {/* Admissions Tab */}
            {activeTab === 'admissions' && (
              <motion.div key="admissions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold" data-testid="admissions-title">Admissions</h2>
                    <p className="text-sm text-muted-foreground">{admissions.length} applications</p>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Total', value: admissions.length, color: 'text-primary' },
                    { label: 'Approved', value: admissions.filter(a => a.admission_status === 'approved').length, color: 'text-green-500' },
                    { label: 'Pending Payment', value: admissions.filter(a => a.admission_status === 'pending_payment').length, color: 'text-yellow-500' },
                    { label: 'Rejected', value: admissions.filter(a => a.admission_status === 'rejected').length, color: 'text-red-500' },
                  ].map(s => (
                    <Card key={s.label} className={cardClass}>
                      <CardContent className="p-4 text-center">
                        <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                        <p className="text-xs text-muted-foreground">{s.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {admissions.length === 0 ? (
                  <Card className={cardClass}>
                    <CardContent className="p-12 text-center">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold mb-1">No applications yet</h3>
                      <p className="text-sm text-muted-foreground">Applications will appear here when students apply</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className={cardClass}>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-3 font-medium text-muted-foreground">Application ID</th>
                              <th className="text-left p-3 font-medium text-muted-foreground">Student</th>
                              <th className="text-left p-3 font-medium text-muted-foreground">Courses</th>
                              <th className="text-left p-3 font-medium text-muted-foreground">Amount</th>
                              <th className="text-left p-3 font-medium text-muted-foreground">Payment</th>
                              <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                              <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                              <th className="text-left p-3 font-medium text-muted-foreground">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {admissions.map(app => (
                              <tr key={app.application_id} className="border-b hover:bg-muted/30">
                                <td className="p-3">
                                  <Badge variant="outline" className="font-mono text-xs">{app.application_id}</Badge>
                                </td>
                                <td className="p-3">
                                  <div>
                                    <p className="font-medium">{app.full_name}</p>
                                    <p className="text-xs text-muted-foreground">{app.email}</p>
                                    <p className="text-xs text-muted-foreground">{app.mobile}</p>
                                  </div>
                                </td>
                                <td className="p-3">
                                  <div className="flex flex-col gap-1">
                                    {(app.selected_courses || []).map(c => (
                                      <Badge key={c.course_id} variant="outline" className="text-xs w-fit">{c.course_id} - {c.title}</Badge>
                                    ))}
                                    {(app.free_courses || []).map(id => (
                                      <Badge key={id} className="bg-green-600 text-white text-xs w-fit">{id} (FREE)</Badge>
                                    ))}
                                  </div>
                                </td>
                                <td className="p-3 font-medium">{'\u20B9'}{(app.total_amount || 0).toLocaleString('en-IN')}</td>
                                <td className="p-3">
                                  <Badge variant={app.payment_status === 'paid' ? 'default' : 'secondary'} className={app.payment_status === 'paid' ? 'bg-green-600' : ''}>
                                    {app.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                                  </Badge>
                                </td>
                                <td className="p-3">
                                  <Badge variant={app.admission_status === 'approved' ? 'default' : app.admission_status === 'rejected' ? 'destructive' : 'secondary'}
                                    className={app.admission_status === 'approved' ? 'bg-green-600' : ''}>
                                    {app.admission_status?.replace('_', ' ')}
                                  </Badge>
                                </td>
                                <td className="p-3 text-xs text-muted-foreground">
                                  {app.created_at ? new Date(app.created_at).toLocaleDateString('en-IN') : '-'}
                                </td>
                                <td className="p-3">
                                  <div className="flex gap-1">
                                    {app.admission_status !== 'approved' && (
                                      <Button variant="outline" size="sm" onClick={async () => {
                                        try {
                                          const headers = getAuthHeaders();
                                          await axios.put(`${API}/admin/admissions/${app.application_id}/status`, { status: 'approved' }, { headers, withCredentials: true });
                                          toast.success('Application approved');
                                          handleRefresh();
                                        } catch (e) { toast.error('Failed'); }
                                      }}>
                                        <Check className="w-3 h-3 mr-1" /> Approve
                                      </Button>
                                    )}
                                    {app.admission_status !== 'rejected' && (
                                      <Button variant="ghost" size="sm" onClick={async () => {
                                        try {
                                          const headers = getAuthHeaders();
                                          await axios.put(`${API}/admin/admissions/${app.application_id}/status`, { status: 'rejected' }, { headers, withCredentials: true });
                                          toast.success('Application rejected');
                                          handleRefresh();
                                        } catch (e) { toast.error('Failed'); }
                                      }}>
                                        <X className="w-3 h-3 mr-1" /> Reject
                                      </Button>
                                    )}
                                    {app.admission_status === 'rejected' && (
                                      <Button variant="destructive" size="sm" onClick={async () => {
                                        if (!window.confirm(`Delete application ${app.application_id}? This is permanent and the student can re-apply.`)) return;
                                        try {
                                          const headers = getAuthHeaders();
                                          await axios.delete(`${API}/admin/admissions/${app.application_id}`, { headers, withCredentials: true });
                                          toast.success('Application deleted permanently');
                                          handleRefresh();
                                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
                                      }} data-testid={`delete-admission-${app.application_id}`}>
                                        <Trash2 className="w-3 h-3 mr-1" /> Delete
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Offers Management Section */}
                <Separator className="my-6" />
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold" data-testid="offers-title">Admission Offers</h2>
                    <p className="text-sm text-muted-foreground">Manage combo deals and discounts</p>
                  </div>
                  <Button onClick={() => { setEditingOffer(null); setShowOfferModal(true); }} data-testid="create-offer-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Offer
                  </Button>
                </div>

                {adminOffers.length === 0 ? (
                  <Card className={cardClass}>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      <Gift className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No offers yet. Create one to attract more students!</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {adminOffers.map(offer => (
                      <Card key={offer.offer_id} className={cardClass}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Gift className={`w-5 h-5 ${offer.active ? 'text-green-500' : 'text-muted-foreground'}`} />
                              <h3 className="font-bold">{offer.name}</h3>
                            </div>
                            <Badge variant={offer.active ? 'default' : 'secondary'}>{offer.active ? 'Active' : 'Inactive'}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">{offer.description}</p>
                          <div className="flex flex-wrap gap-1 mb-2">
                            {(offer.required_courses || []).map(cid => <Badge key={cid} variant="outline" className="text-xs font-mono">{cid}</Badge>)}
                          </div>
                          <div className="text-sm space-y-1">
                            {offer.discount_amount > 0 && <p>Discount: <strong>{'\u20B9'}{offer.discount_amount.toLocaleString('en-IN')}</strong></p>}
                            {offer.free_course_id && <p>Free course: <Badge variant="outline" className="text-xs font-mono">{offer.free_course_id}</Badge></p>}
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button variant="outline" size="sm" onClick={() => { setEditingOffer(offer); setShowOfferModal(true); }}>
                              <Edit className="w-3 h-3 mr-1" /> Edit
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteOffer(offer.offer_id)}>
                              <Trash2 className="w-3 h-3 mr-1 text-destructive" /> Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

              </motion.div>
            )}
            {activeTab === 'students' && (
              <motion.div key="students" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold">Student Management ({students.filter(s => !s.is_test_account).length} real, {students.filter(s => s.is_test_account).length} test)</h2>
                  <p className="text-sm text-muted-foreground">Students enroll via course pages on the website</p>
                </div>

                <Card className={cardClass}>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-4 font-medium">Student</th>
                            <th className="text-left p-4 font-medium">Course</th>
                            <th className="text-left p-4 font-medium">Fees</th>
                            <th className="text-left p-4 font-medium">Status</th>
                            <th className="text-left p-4 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredStudents.map(student => {
                            const isTestAccount = student.is_test_account || 
                              (student.email && (
                                student.email.includes('@example.com') ||
                                student.email.includes('@test.com') ||
                                student.email.toLowerCase().startsWith('test')
                              ));
                            return (
                            <tr key={student.user_id} className={`border-b hover:bg-muted/50 ${isTestAccount ? 'opacity-60' : ''}`}>
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <Avatar>
                                    <AvatarFallback className={`${isTestAccount ? 'bg-yellow-500/10 text-yellow-600' : 'bg-blue-500/10 text-blue-500'}`}>
                                      {student.name?.split(' ').map(n => n[0]).join('')}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium">{student.name}</p>
                                      {isTestAccount && (
                                        <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-600 border-yellow-500/30">TEST</Badge>
                                      )}
                                      {student.assigned_mentor_name && (
                                        <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/30">
                                          Mentor: {student.assigned_mentor_name}
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">{student.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                                {student.enrollments?.[0]?.course_title || student.enrollments?.[0]?.course_id || '-'}
                              </td>
                              <td className="p-4">
                                <div>
                                  <p className="font-medium">₹{(student.fees_paid || 0).toLocaleString()}</p>
                                  <p className="text-xs text-muted-foreground">of ₹{(student.total_fees || 0).toLocaleString()}</p>
                                  <Progress value={student.total_fees ? (student.fees_paid / student.total_fees) * 100 : 0} className="h-1 mt-1" />
                                </div>
                              </td>
                              <td className="p-4">
                                <Badge variant={student.status === 'active' ? 'default' : 'secondary'}>{student.status || 'active'}</Badge>
                              </td>
                              <td className="p-4">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem onClick={() => setViewingStudent(student)}>
                                      <Eye className="w-4 h-4 mr-2" /> View Details
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => { setEditingStudent(student); setIsNewStudent(false); }}>
                                      <Edit className="w-4 h-4 mr-2" /> Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      onClick={() => setDeletingStudent(student)}
                                      className="text-destructive focus:text-destructive"
                                      data-testid={`delete-student-${student.user_id}`}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" /> Delete Student
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            </tr>
                          )})}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Mentors Tab */}
            {activeTab === 'mentors' && (
              <motion.div key="mentors" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h2 className="text-2xl font-bold">Mentor Management ({mentors.length})</h2>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {mentors.map(mentor => (
                    <Card key={mentor.user_id} className={cardClass}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <Avatar className="w-14 h-14">
                            <AvatarFallback className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-lg">
                              {mentor.name?.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h3 className="font-bold">{mentor.name}</h3>
                            <p className="text-sm text-muted-foreground">{mentor.email}</p>
                            {mentor.vendor_id && <Badge variant="outline" className="mt-1 text-xs">{mentor.vendor_id}</Badge>}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => setDeletingMentor(mentor)}
                            data-testid={`delete-mentor-${mentor.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Skills */}
                        <div className="flex flex-wrap gap-1 mt-4">
                          {(mentor.skills || mentor.subjects || []).map((s, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                          ))}
                        </div>

                        {/* Course Assignments */}
                        {mentor.course_assignments?.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-sm font-medium mb-2">Assigned Courses:</p>
                            {mentor.course_assignments.map((a, i) => (
                              <div key={i} className="text-sm text-muted-foreground">
                                {a.course_title} - <span className="text-primary">{a.skill}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
                          <div className="text-center">
                            <p className="text-xl font-bold text-blue-500">{mentor.assigned_students || 0}</p>
                            <p className="text-xs text-muted-foreground">Students</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xl font-bold text-yellow-500 flex items-center justify-center gap-1">
                              <Star className="w-4 h-4 fill-yellow-500" />{mentor.rating || 0}
                            </p>
                            <p className="text-xs text-muted-foreground">Rating</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Finances Tab - Using New FinanceOverview Component */}
            {activeTab === 'finances' && (
              <FinanceOverview theme={theme} />
            )}

            {/* Announcements Tab */}
            {activeTab === 'announcements' && (
              <motion.div key="announcements" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold">Announcements</h2>
                  <Button onClick={() => setShowAnnouncementModal(true)}>
                    <Megaphone className="w-4 h-4 mr-2" /> New Announcement
                  </Button>
                </div>

                {/* Target Audience Filters */}
                <div className="flex gap-3">
                  <Badge variant="outline" className="px-4 py-2 cursor-pointer hover:bg-primary/10">All ({announcements.length})</Badge>
                  <Badge variant="outline" className="px-4 py-2 cursor-pointer hover:bg-primary/10">
                    <Users className="w-3 h-3 mr-1" /> Students ({announcements.filter(a => a.target === 'students').length})
                  </Badge>
                  <Badge variant="outline" className="px-4 py-2 cursor-pointer hover:bg-primary/10">
                    <GraduationCap className="w-3 h-3 mr-1" /> Mentors ({announcements.filter(a => a.target === 'mentors').length})
                  </Badge>
                </div>
                
                {/* Announcements List */}
                <div className="space-y-4">
                  {announcements.length > 0 ? (
                    announcements.map(a => (
                      <Card key={a.announcement_id} className={cardClass}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-bold text-lg">{a.title}</h3>
                                <Badge className={
                                  a.target === 'students' ? 'bg-blue-500/10 text-blue-600' :
                                  a.target === 'mentors' ? 'bg-purple-500/10 text-purple-600' :
                                  'bg-emerald-500/10 text-emerald-600'
                                }>
                                  {a.target === 'students' ? 'Students' : a.target === 'mentors' ? 'Mentors' : 'Everyone'}
                                </Badge>
                                <Badge variant={a.status === 'published' ? 'default' : 'secondary'}>
                                  {a.status}
                                </Badge>
                              </div>
                              <p className="text-muted-foreground mb-3">{a.content}</p>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {new Date(a.created_at).toLocaleDateString()}
                                </span>
                                {a.target_course_id && (
                                  <span className="flex items-center gap-1">
                                    <BookOpen className="w-4 h-4" />
                                    Course: {a.target_course_id}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteAnnouncement(a.announcement_id)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Card className={cardClass}>
                      <CardContent className="p-8 text-center text-muted-foreground">
                        <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <h3 className="font-medium text-lg mb-2">No Announcements Yet</h3>
                        <p className="mb-4">Create your first announcement to communicate with students and mentors</p>
                        <Button onClick={() => setShowAnnouncementModal(true)}>
                          <Plus className="w-4 h-4 mr-2" /> Create Announcement
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Announcement Modal */}
                {showAnnouncementModal && (
                  <Dialog open={true} onOpenChange={() => setShowAnnouncementModal(false)}>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Megaphone className="w-5 h-5" />
                          New Announcement
                        </DialogTitle>
                        <DialogDescription>
                          Send an announcement to students, mentors, or everyone
                        </DialogDescription>
                      </DialogHeader>
                      
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Input
                            placeholder="Announcement title..."
                            value={newAnnouncement.title}
                            onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Message</Label>
                          <Textarea
                            placeholder="Write your announcement message..."
                            rows={4}
                            value={newAnnouncement.content}
                            onChange={(e) => setNewAnnouncement(prev => ({ ...prev, content: e.target.value }))}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Send To</Label>
                          <div className="grid grid-cols-3 gap-3">
                            {[
                              { value: 'students', label: 'Students', icon: Users, color: 'blue' },
                              { value: 'mentors', label: 'Mentors', icon: GraduationCap, color: 'purple' },
                              { value: 'everyone', label: 'Everyone', icon: Megaphone, color: 'emerald' }
                            ].map(option => (
                              <div
                                key={option.value}
                                onClick={() => setNewAnnouncement(prev => ({ ...prev, target: option.value }))}
                                className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                                  newAnnouncement.target === option.value
                                    ? `border-${option.color}-500 bg-${option.color}-500/10`
                                    : 'border-muted hover:border-muted-foreground/30'
                                }`}
                              >
                                <option.icon className={`w-6 h-6 mx-auto mb-2 ${
                                  newAnnouncement.target === option.value ? `text-${option.color}-500` : 'text-muted-foreground'
                                }`} />
                                <p className="font-medium text-sm">{option.label}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAnnouncementModal(false)}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleCreateAnnouncement}
                          disabled={!newAnnouncement.title || !newAnnouncement.content}
                          className="bg-gradient-to-r from-emerald-500 to-cyan-500"
                        >
                          <Megaphone className="w-4 h-4 mr-2" />
                          Publish Announcement
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </motion.div>
            )}

            {/* Alumni Tab */}
            {activeTab === 'alumni' && (
              <motion.div key="alumni" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold">Alumni Management</h2>
                  <Button onClick={() => { setEditingAlumni({}); setIsNewAlumni(true); }}>
                    <Plus className="w-4 h-4 mr-2" /> Add Alumni
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className={cardClass}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                        <Award className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{alumni.length}</p>
                        <p className="text-sm text-muted-foreground">Total Alumni</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={cardClass}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                        <Star className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{alumni.filter(a => a.is_featured).length}</p>
                        <p className="text-sm text-muted-foreground">Featured</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={cardClass}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{[...new Set(alumni.map(a => a.current_company))].length}</p>
                        <p className="text-sm text-muted-foreground">Companies</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={cardClass}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                        <GraduationCap className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{[...new Set(alumni.map(a => a.batch_year))].length}</p>
                        <p className="text-sm text-muted-foreground">Batches</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Alumni List */}
                <Card className={cardClass}>
                  <CardHeader>
                    <CardTitle>All Alumni</CardTitle>
                    <CardDescription>Manage alumni records and feature them on homepage</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {alumni.length > 0 ? (
                      <div className="space-y-4">
                        {alumni.map((alum) => (
                          <div key={alum.alumni_id} className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                                {alum.name?.charAt(0)}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold">{alum.name}</h4>
                                  {alum.is_featured && (
                                    <Badge className="bg-amber-500/10 text-amber-600">
                                      <Star className="w-3 h-3 mr-1" /> Featured
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {alum.current_role} at {alum.current_company}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {alum.course_completed} • Batch {alum.batch_year}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {alum.linkedin_url && (
                                <Button variant="ghost" size="sm" asChild>
                                  <a href={alum.linkedin_url} target="_blank" rel="noopener noreferrer">
                                    <Linkedin className="w-4 h-4" />
                                  </a>
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => { setEditingAlumni(alum); setIsNewAlumni(false); }}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteAlumni(alum.alumni_id)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Award className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                        <h3 className="font-semibold text-lg mb-2">No Alumni Records</h3>
                        <p className="text-muted-foreground mb-4">Start adding alumni to showcase your successful graduates</p>
                        <Button onClick={() => { setEditingAlumni({}); setIsNewAlumni(true); }}>
                          <Plus className="w-4 h-4 mr-2" /> Add First Alumni
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Alumni Modal */}
                {editingAlumni && (
                  <Dialog open={true} onOpenChange={() => { setEditingAlumni(null); setIsNewAlumni(false); }}>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <Award className="w-5 h-5" />
                          {isNewAlumni ? 'Add New Alumni' : 'Edit Alumni'}
                        </DialogTitle>
                        <DialogDescription>
                          Add successful graduates to showcase on homepage
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Full Name *</Label>
                            <Input
                              value={editingAlumni.name || ''}
                              onChange={e => setEditingAlumni({...editingAlumni, name: e.target.value})}
                              placeholder="Enter full name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Batch Year *</Label>
                            <Input
                              value={editingAlumni.batch_year || ''}
                              onChange={e => setEditingAlumni({...editingAlumni, batch_year: e.target.value})}
                              placeholder="e.g., 2023"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Course Completed *</Label>
                          <Select
                            value={editingAlumni.course_completed || ''}
                            onValueChange={v => setEditingAlumni({...editingAlumni, course_completed: v})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select course" />
                            </SelectTrigger>
                            <SelectContent>
                              {courses.map(c => (
                                <SelectItem key={c.course_id} value={c.course_id}>{c.course_id} - {c.title}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Current Company *</Label>
                            <Input
                              value={editingAlumni.current_company || ''}
                              onChange={e => setEditingAlumni({...editingAlumni, current_company: e.target.value})}
                              placeholder="e.g., Pixar Studios"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Current Role *</Label>
                            <Input
                              value={editingAlumni.current_role || ''}
                              onChange={e => setEditingAlumni({...editingAlumni, current_role: e.target.value})}
                              placeholder="e.g., 3D Artist"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                              type="email"
                              value={editingAlumni.email || ''}
                              onChange={e => setEditingAlumni({...editingAlumni, email: e.target.value})}
                              placeholder="email@example.com"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input
                              value={editingAlumni.phone || ''}
                              onChange={e => setEditingAlumni({...editingAlumni, phone: e.target.value})}
                              placeholder="+91 9999999999"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>LinkedIn URL</Label>
                          <Input
                            value={editingAlumni.linkedin_url || ''}
                            onChange={e => setEditingAlumni({...editingAlumni, linkedin_url: e.target.value})}
                            placeholder="https://linkedin.com/in/username"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Testimonial</Label>
                          <Textarea
                            value={editingAlumni.testimonial || ''}
                            onChange={e => setEditingAlumni({...editingAlumni, testimonial: e.target.value})}
                            placeholder="Share their success story..."
                            rows={3}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="is_featured"
                            checked={editingAlumni.is_featured || false}
                            onChange={e => setEditingAlumni({...editingAlumni, is_featured: e.target.checked})}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <Label htmlFor="is_featured" className="cursor-pointer">
                            Feature on Homepage
                          </Label>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => { setEditingAlumni(null); setIsNewAlumni(false); }}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveAlumni}>
                          {isNewAlumni ? 'Add Alumni' : 'Save Changes'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </motion.div>
            )}

            {/* Coupons Tab */}
            {activeTab === 'coupons' && (
              <motion.div key="coupons" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">Coupon Management</h2>
                    <p className="text-muted-foreground">Create discount codes for your courses</p>
                  </div>
                  <Button onClick={() => { setEditingCoupon({}); setIsNewCoupon(true); }}>
                    <Plus className="w-4 h-4 mr-2" /> Create Coupon
                  </Button>
                </div>

                <Card className={cardClass}>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-4 font-medium">Code</th>
                            <th className="text-left p-4 font-medium">Discount</th>
                            <th className="text-left p-4 font-medium">Usage</th>
                            <th className="text-left p-4 font-medium">Validity</th>
                            <th className="text-left p-4 font-medium">Status</th>
                            <th className="text-left p-4 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {coupons.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="p-8 text-center text-muted-foreground">
                                <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>No coupons created yet</p>
                                <Button variant="outline" className="mt-3" onClick={() => { setEditingCoupon({}); setIsNewCoupon(true); }}>
                                  <Plus className="w-4 h-4 mr-2" /> Create Your First Coupon
                                </Button>
                              </td>
                            </tr>
                          ) : coupons.map(coupon => (
                            <tr key={coupon.coupon_id} className="border-b hover:bg-muted/50">
                              <td className="p-4">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="font-mono text-sm">{coupon.code}</Badge>
                                </div>
                              </td>
                              <td className="p-4">
                                {coupon.discount_type === 'percentage' 
                                  ? `${coupon.discount_value}% OFF`
                                  : `₹${coupon.discount_value.toLocaleString()} OFF`
                                }
                              </td>
                              <td className="p-4">
                                <div className="text-sm">
                                  <span className="font-medium">{coupon.usage_count || 0}</span>
                                  {coupon.usage_limit && <span className="text-muted-foreground"> / {coupon.usage_limit}</span>}
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="text-sm">
                                  {coupon.valid_until ? (
                                    <span className={new Date(coupon.valid_until) < new Date() ? 'text-red-500' : ''}>
                                      Until {new Date(coupon.valid_until).toLocaleDateString()}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">No expiry</span>
                                  )}
                                </div>
                              </td>
                              <td className="p-4">
                                <Badge variant={coupon.is_active ? 'default' : 'secondary'}>
                                  {coupon.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-2">
                                  <Button variant="ghost" size="sm" onClick={() => { setEditingCoupon(coupon); setIsNewCoupon(false); }}>
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteCoupon(coupon.coupon_id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Coupon Modal */}
                {editingCoupon && (
                  <Dialog open={true} onOpenChange={() => { setEditingCoupon(null); setIsNewCoupon(false); }}>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{isNewCoupon ? 'Create New Coupon' : 'Edit Coupon'}</DialogTitle>
                        <DialogDescription>
                          {isNewCoupon ? 'Create a discount code for students' : 'Update coupon settings'}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Coupon Code *</Label>
                          <Input 
                            value={editingCoupon.code || ''} 
                            onChange={e => setEditingCoupon({...editingCoupon, code: e.target.value.toUpperCase()})}
                            placeholder="e.g., EARLY50, STUDENT20"
                            className="font-mono"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Discount Type</Label>
                            <Select 
                              value={editingCoupon.discount_type || 'percentage'} 
                              onValueChange={v => setEditingCoupon({...editingCoupon, discount_type: v})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">Percentage (%)</SelectItem>
                                <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Discount Value *</Label>
                            <Input 
                              type="number"
                              value={editingCoupon.discount_value || ''} 
                              onChange={e => setEditingCoupon({...editingCoupon, discount_value: parseFloat(e.target.value) || 0})}
                              placeholder={editingCoupon.discount_type === 'fixed' ? '5000' : '20'}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Usage Limit (optional)</Label>
                            <Input 
                              type="number"
                              value={editingCoupon.usage_limit || ''} 
                              onChange={e => setEditingCoupon({...editingCoupon, usage_limit: parseInt(e.target.value) || null})}
                              placeholder="100"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Max Discount (₹)</Label>
                            <Input 
                              type="number"
                              value={editingCoupon.max_discount || ''} 
                              onChange={e => setEditingCoupon({...editingCoupon, max_discount: parseFloat(e.target.value) || null})}
                              placeholder="10000"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Valid Until (optional)</Label>
                          <Input 
                            type="date"
                            value={editingCoupon.valid_until ? editingCoupon.valid_until.split('T')[0] : ''} 
                            onChange={e => setEditingCoupon({...editingCoupon, valid_until: e.target.value ? new Date(e.target.value).toISOString() : null})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Applicable Tiers</Label>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input 
                                type="checkbox" 
                                checked={(editingCoupon.applicable_tiers || ['self_learning', 'instructor_led']).includes('self_learning')}
                                onChange={e => {
                                  const tiers = editingCoupon.applicable_tiers || ['self_learning', 'instructor_led'];
                                  setEditingCoupon({
                                    ...editingCoupon, 
                                    applicable_tiers: e.target.checked 
                                      ? [...tiers, 'self_learning'].filter((v, i, a) => a.indexOf(v) === i)
                                      : tiers.filter(t => t !== 'self_learning')
                                  });
                                }}
                                className="rounded"
                              />
                              Self-Learning
                            </label>
                            <label className="flex items-center gap-2">
                              <input 
                                type="checkbox" 
                                checked={(editingCoupon.applicable_tiers || ['self_learning', 'instructor_led']).includes('instructor_led')}
                                onChange={e => {
                                  const tiers = editingCoupon.applicable_tiers || ['self_learning', 'instructor_led'];
                                  setEditingCoupon({
                                    ...editingCoupon, 
                                    applicable_tiers: e.target.checked 
                                      ? [...tiers, 'instructor_led'].filter((v, i, a) => a.indexOf(v) === i)
                                      : tiers.filter(t => t !== 'instructor_led')
                                  });
                                }}
                                className="rounded"
                              />
                              Instructor-Led
                            </label>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            checked={editingCoupon.is_active !== false}
                            onChange={e => setEditingCoupon({...editingCoupon, is_active: e.target.checked})}
                            className="rounded"
                          />
                          <Label>Active</Label>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => { setEditingCoupon(null); setIsNewCoupon(false); }}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveCoupon}>
                          {isNewCoupon ? 'Create Coupon' : 'Save Changes'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </motion.div>
            )}

            {/* Call Center Tab */}
            {activeTab === 'call-center' && (
              <CallCenter token={localStorage.getItem('auth_token')} />
            )}

            {/* Leads CRM Tab */}
            {activeTab === 'leads' && (
              <LeadsCRM />
            )}

            {/* Sia Training Tab */}
            {activeTab === 'sia-training' && (
              <SiaTraining />
            )}

            {/* Team Management Tab */}
            {activeTab === 'team' && (
              <TeamManagement />
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <SiteSettings theme={theme} />
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Modals */}
      {editingCourse && (
        <CourseModal
          course={isNewCourse ? (editingCourse.category_id ? editingCourse : null) : editingCourse}
          courses={courses}
          mentors={mentors}
          categories={categories}
          onClose={() => { setEditingCourse(null); setIsNewCourse(false); }}
          onSave={handleSaveCourse}
          isNew={isNewCourse}
        />
      )}

      {/* Category Create/Edit Modal */}
      {showCategoryModal && (
        <CategoryModal
          category={editingCategory}
          onClose={() => { setShowCategoryModal(false); setEditingCategory(null); }}
          onSave={handleSaveCategory}
        />
      )}

      {showOfferModal && (
        <OfferModal
          offer={editingOffer}
          courses={courses}
          onClose={() => { setShowOfferModal(false); setEditingOffer(null); }}
          onSave={handleSaveOffer}
        />
      )}


      {editingStudent && (
        <StudentModal
          student={isNewStudent ? null : editingStudent}
          courses={courses}
          mentors={mentors}
          onClose={() => { setEditingStudent(null); setIsNewStudent(false); }}
          onSave={handleSaveStudent}
          isNew={isNewStudent}
        />
      )}

      {viewingStudent && (
        <StudentDetailModal
          student={viewingStudent}
          onClose={() => setViewingStudent(null)}
        />
      )}

      {assigningMentor && (
        <MentorAssignmentModal
          course={assigningMentor}
          mentors={mentors}
          existingAssignments={assigningMentor.assigned_mentors || []}
          onClose={() => setAssigningMentor(null)}
          onAssign={handleAssignMentor}
        />
      )}

      {/* Direct Mentor Assignment to Student Enrollment Modal */}
      {assigningMentorToEnrollment && (
        <Dialog open={true} onOpenChange={() => setAssigningMentorToEnrollment(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Assign Mentor to {assigningMentorToEnrollment.student_name}
              </DialogTitle>
              <DialogDescription>
                Select a mentor for the course: {assigningMentorToEnrollment.course_title || assigningMentorToEnrollment.course_id}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Label className="mb-3 block">Available Mentors</Label>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {mentors.length > 0 ? (
                  mentors.map(mentor => (
                    <div 
                      key={mentor.user_id} 
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => handleAssignMentorToEnrollment(assigningMentorToEnrollment, mentor.user_id)}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                            {mentor.name?.charAt(0) || 'M'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{mentor.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(mentor.skills || mentor.subjects || []).slice(0, 3).join(', ')}
                          </p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline">
                        Select
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No mentors available</p>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAssigningMentorToEnrollment(null)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Mentor Confirmation Dialog */}
      {deletingMentor && (
        <Dialog open={true} onOpenChange={() => setDeletingMentor(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="w-5 h-5" />
                Delete Mentor
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this mentor? This action will:
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="flex items-center gap-3 mb-4 p-3 bg-muted rounded-lg">
                <Avatar className="w-12 h-12">
                  <AvatarFallback className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                    {deletingMentor.name?.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{deletingMentor.name}</p>
                  <p className="text-sm text-muted-foreground">{deletingMentor.email}</p>
                </div>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li>Deactivate the mentor account</li>
                <li>Remove all course assignments</li>
                <li>This action can be reversed by reactivating the account</li>
              </ul>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeletingMentor(null)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteMentor}
                disabled={isDeleting}
                data-testid="confirm-delete-mentor"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Mentor
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Student Confirmation Dialog */}
      {deletingStudent && (
        <Dialog open={true} onOpenChange={() => setDeletingStudent(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="w-5 h-5" />
                Delete Student
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this student? This action will deactivate their account.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="flex items-center gap-3 mb-4 p-3 bg-muted rounded-lg">
                <Avatar className="w-12 h-12">
                  <AvatarFallback className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
                    {deletingStudent.name?.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{deletingStudent.name}</p>
                  <p className="text-sm text-muted-foreground">{deletingStudent.email}</p>
                </div>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li>Deactivate the student account</li>
                <li>Student will no longer be able to login</li>
                <li>Enrollment data will be preserved</li>
                <li>This action can be reversed by reactivating the account</li>
              </ul>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeletingStudent(null)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteStudent}
                disabled={isDeleting}
                data-testid="confirm-delete-student"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Student
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
